import React from 'react';
import PropTypes from 'prop-types';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { makeStyles } from 'tss-react/mui';
import { semanticColors } from '../../theme/tokens/colors';

const BORDER_VARIANTS = {
    full: 'full',
    bottomWhenCollapsed: 'bottom-when-collapsed',
};

const useAccordionStyles = makeStyles()((theme, { borderVariant, disableTopMargin }) => {
    const isBottomWhenCollapsed = borderVariant === BORDER_VARIANTS.bottomWhenCollapsed;
    const collapsedMarginTop = disableTopMargin ? 0 : (isBottomWhenCollapsed ? theme.spacing(0.5) : theme.spacing(1.5));
    const rootBorder = `1.5px solid ${semanticColors.borderSubtleStrong}`;
    return {
        root: {
            width: '100%',
            backgroundColor: semanticColors.appBackground,
            border: isBottomWhenCollapsed ? 'none' : rootBorder,
            borderTop: isBottomWhenCollapsed ? 'none' : undefined,
            borderRight: isBottomWhenCollapsed ? 'none' : undefined,
            borderLeft: isBottomWhenCollapsed ? 'none' : undefined,
            borderBottom: isBottomWhenCollapsed ? 'none' : rootBorder,
            borderRadius: isBottomWhenCollapsed ? 0 : 8,
            boxShadow: 'none',
            marginTop: collapsedMarginTop,
            '&:before': {
                display: 'none',
            },
            '&.Mui-expanded': {
                margin: `${collapsedMarginTop}px 0 0 0`,
                border: isBottomWhenCollapsed ? 'none' : rootBorder,
                borderTop: isBottomWhenCollapsed ? 'none' : undefined,
                borderRight: isBottomWhenCollapsed ? 'none' : undefined,
                borderLeft: isBottomWhenCollapsed ? 'none' : undefined,
                borderBottom: isBottomWhenCollapsed ? 'none' : rootBorder,
                borderRadius: isBottomWhenCollapsed ? 0 : 8,
            },
        },
        rounded: {
            borderRadius: isBottomWhenCollapsed ? 0 : 8,
        },
        expanded: {},
    };
});

const useSummaryStyles = makeStyles()((theme) => ({
    root: {
        minHeight: theme.spacing(6),
        '&.Mui-expanded': {
            minHeight: theme.spacing(6),
        },
    },
    content: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        '&.Mui-expanded': {
            marginTop: theme.spacing(1),
            marginBottom: theme.spacing(1),
        },
    },
    expanded: {},
}));

const useDetailsStyles = makeStyles()((theme) => ({
    root: {
        paddingTop: 0,
        paddingBottom: theme.spacing(1.5),
    },
}));

export const AppAccordion = ({ children, className, classes: customClasses, borderVariant = BORDER_VARIANTS.full, disableTopMargin = false, ...props }) => {
    const { classes } = useAccordionStyles({ borderVariant, disableTopMargin });
    return (
        <Accordion
            className={className}
            classes={{
                root: `${classes.root} ${customClasses?.root || ''}`.trim(),
                rounded: `${classes.rounded} ${customClasses?.rounded || ''}`.trim(),
                expanded: `${classes.expanded} ${customClasses?.expanded || ''}`.trim(),
            }}
            {...props}>
            {children}
        </Accordion>
    );
};

export const AppAccordionSummary = ({ children, className, classes: customClasses, ...props }) => {
    const { classes } = useSummaryStyles();
    return (
        <AccordionSummary
            className={className}
            classes={{
                root: `${classes.root} ${customClasses?.root || ''}`.trim(),
                content: `${classes.content} ${customClasses?.content || ''}`.trim(),
                expanded: `${classes.expanded} ${customClasses?.expanded || ''}`.trim(),
            }}
            {...props}>
            {children}
        </AccordionSummary>
    );
};

export const AppAccordionDetails = ({ children, className, classes: customClasses, ...props }) => {
    const { classes } = useDetailsStyles();
    return (
        <AccordionDetails className={className} classes={{ root: `${classes.root} ${customClasses?.root || ''}`.trim() }} {...props}>
            {children}
        </AccordionDetails>
    );
};

AppAccordion.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    classes: PropTypes.object,
    borderVariant: PropTypes.oneOf([BORDER_VARIANTS.full, BORDER_VARIANTS.bottomWhenCollapsed]),
    disableTopMargin: PropTypes.bool,
};

AppAccordionSummary.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    classes: PropTypes.object,
};

AppAccordionDetails.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    classes: PropTypes.object,
};

export default AppAccordion;